/** /activity-categories/:id/:tab? — the activity category record (plan §1, §2, §4, §6, §9.7,
 *  §10.3; O8: a small record — a header, one card, an Activities panel): a heading in the
 *  app's activity browser, read first and edited in place on the records core — the activity
 *  record's sibling.
 *
 *  - **Header** — `CategoryRecordHeader`: the icon (downsized, opening the original), the
 *    title, tenant · ID; the Actions menu (Investigate: Open in Activities, Open in dashboard;
 *    Danger: Delete…) and **Edit** (`E`) / **Close editor** (`Esc`). No
 *    console_activities_manage → no Edit; the `ConsoleCatalogEditEnabled` switch off → Edit
 *    disabled, "Editing is switched off"; an editor that could not load → Edit disabled with
 *    that reason, and a banner with Retry.
 *  - **Tabs** (URL, keeping `?edit`): Category (the record) · History (console_audit_view, the
 *    existing panel). Old slugs redirect to the record: `general`, and `used-by` — the
 *    category's activities, which the Activities panel now lists.
 *  - **The card** (key `1`) — Title, Icon, Access tags; System: Activities — FieldSlots over
 *    the registry, the values from the draft's baselines: edit_context's, else the detail's
 *    (the icon's then drawn from the detail alone — its Image id comes with edit_context).
 *  - **Activities in this category** (key `2`) — the first ten by name, each a link, then
 *    the rest in the activities grid. Its rows are asked for only once the panel has been on
 *    screen, never with the detail.
 *  - **Edit mode** — exactly the record editor: `?edit=1` (or `E`) once the edit catalog and
 *    edit_context are here; the draft (`DraftProvider kind="activity_category"`, seeded from
 *    edit_context `baselines`) and the editing session are PAGE-level, so a tab switch keeps
 *    both and only leaving the category is guarded. FieldSlots swap in their inputs in place
 *    (Zero-Re-Layout). The icon: a picked file uploads at once (`POST /media/staged/`,
 *    `category_icon`) and the draft holds the Image id it answers (D3); the input is the
 *    view's 48 px tile. The preview plans the draft (the ledger's lines, the category's app
 *    cache, the replaced icon); Save is the apply, compare-and-set per field: a 409 turns the
 *    drifted fields stale (Keep mine / Take theirs), a 400 files its errors under their fields
 *    — an icon the daily sweep removed offers "Upload it again" — and a save ends in the
 *    receipt with the audit link and re-seeds the page.
 *  - **The dock: Changes, in both modes** (O5 without its Preview: a category has no app
 *    preview of its own, §4 — `/mobile_preview/` renders tours, itineraries and activities).
 *    From 1100 px of content an operator who may manage the category gets the plain 340 px
 *    ledger column beside the content on every tab but History in view mode too, titled
 *    "Changes" and saying what it will hold (or that editing is switched off, or the changes
 *    a kept draft holds); pressing E turns the same element into the draft's ledger, so view
 *    and edit share one geometry (Zero-Re-Layout) — the card's field grid is pinned to that
 *    width (`content − 340 − 16`). Below 1100 px the ledger is the bottom sheet, in edit mode
 *    only. A viewer, who never edits, gets no dock. History docks the ledger in edit mode
 *    only. Whether the dock stands is decided by the permissions known at the first render
 *    (the skeleton's): edit_context arriving later never adds or removes it — a refusal it
 *    carries is said in the dock instead.
 *  - **States** — a page-shaped skeleton while loading, the tab's (History: one panel, no
 *    dock); the catalog records' empty states
 *    for a missing category (404) or one the operator may not see (403); a load error with
 *    Retry; a failed refresh keeps the loaded copy under a notice; a draft restored from
 *    sessionStorage (or kept after leaving edit mode) is announced in view mode.
 *  - **Delete** — Actions → Danger → Delete…, in both modes, confirms first, naming the
 *    consequence ("12 activities lose their category") and, in a draft that holds changes,
 *    that they go with it; the server un-categorises the activities (they stay) and may
 *    refuse (`409 category_in_use`: something else still refers to it) — the page says so in
 *    its own words. A delete clears the draft and its stored copy before leaving, so the
 *    unsaved-changes guard never asks about a category that no longer exists.
 *  - **Keyboard** — `E` opens the editor, `1` / `2` reach the card and the panel; in edit mode
 *    the ledger binds ⌘S (save), ⌘Z / ⇧⌘Z and Esc (close: a clean draft exits, a dirty one
 *    asks Save · Discard · Keep editing). */
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Alert, Box, Button, Card, CardContent, Skeleton, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Navigate, Link as RouterLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import * as categoriesApi from '@/api/activityCategories';
import type { ActivityCategoryDetail } from '@/api/activityCategories';
import {
  cachedActivityCount,
  useActivityCategoryDetail,
  useActivityCategoryEditCatalog,
  useActivityCategoryEditContext,
  useCategoryActivities,
} from '@/api/activityCategoryRecord';
import { ConsoleApiError } from '@/api/client';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import {
  ACTIVITY_CATEGORY_RECORD,
  CatalogHeaderSkeleton,
  CatalogLoadError,
  CatalogRefreshError,
} from '@/features/catalog/CatalogLoadStates';
import { CATALOG_EDITOR_LOAD_FAILED, CATALOG_EDITING_OFF } from '@/features/catalog/editGate';
import type { RecordKey } from '@/features/records/draft/changeSet';
import { DraftProvider, useDraft, useDraftSummary, useDraftUnsavedGuard } from '@/features/records/draft/DraftProvider';
import { useRecordShortcuts } from '@/features/records/draft/useRecordShortcuts';
import { RecordEditorProvider, useRecordEditor } from '@/features/records/editor/RecordEditorProvider';
import { Ledger } from '@/features/records/ledger/Ledger';
import { LEDGER_GAP_PX, LEDGER_WIDTH, LedgerDock } from '@/features/records/ledger/LedgerDock';
import type { RecordActionGroup } from '@/features/records/page/RecordActionsMenu';
import { RecordEmptyState } from '@/features/records/page/RecordStates';
import {
  APPBAR_PX,
  DOCK_GAP_PX,
  prefersReducedMotion,
  RecordPageTabs,
  retryUnlessMissing,
  SCROLLER_PADDING_PX,
  useElementWidth,
  useRecordNav,
  type RecordNav,
} from '@/features/records/page/recordPage';
import { useSeenOnce } from '@/features/records/page/useSeenOnce';
import { spineColumns } from '@/features/records/views/spineColumns';
import { usePermissions } from '@/hooks/usePermissions';
import { activityCategoriesHref } from '@/utils/gridLinks';
import { formatZoned } from '@/utils/zonedTime';

import { CategoryCard, categorySectionAnchorId } from './CategoryCard';
import {
  ActivitiesInCategoryPanel,
  ActivitiesInCategoryPanelSkeleton,
  CATEGORY_ACTIVITIES_ANCHOR_ID,
} from './parts/ActivitiesInCategoryPanel';
import {
  ACTIVITIES_FIRST,
  activityCountLabel,
  categoryActivitiesHref,
  categoryActivityCount,
} from './parts/activitiesInCategoryModel';
import { CategoryRecordHeader, categoryTitle } from './parts/CategoryRecordHeader';
import {
  CATEGORY_MANAGE_PERM,
  CATEGORY_TIMEZONE,
  categoryBaselines,
  categoryEditBlockedReason,
  useCategoryEditorConfig,
} from './editorConfig';

const MANAGE_PERM = CATEGORY_MANAGE_PERM;

export type CategoryRecordTab = 'category' | 'history';

/** Old tab slugs (bookmarks, links from other pages) → the tab that took them over. */
export const CATEGORY_SLUG_REDIRECTS: Readonly<Record<string, CategoryRecordTab>> = {
  general: 'category',
  // The category's activities: the record's Activities panel lists them now.
  'used-by': 'category',
};

const SECTION_LABEL: Readonly<Record<CategoryRecordTab, string>> = {
  category: 'Category',
  history: 'History',
};

const TABS_LABELS = { label: 'Category sections', refreshingLabel: 'Refreshing the category' };

/** The tabs a viewer sees. */
export function categoryTabs(canViewHistory: boolean): { id: CategoryRecordTab; label: string }[] {
  return [
    { id: 'category', label: 'Category' },
    ...(canViewHistory ? [{ id: 'history' as const, label: 'History' }] : []),
  ];
}

/** The delete's confirmation: what goes, what happens to the activities in it (they lose
 *  their category; they are not deleted) and to the draft's unsaved changes (they go too). */
export function categoryDeleteMessage(title: string, activities: number, unsaved = 0): string {
  const consequence =
    activities === 1
      ? ' 1 activity loses its category — the activity itself stays.'
      : activities > 1
        ? ` ${activityCountLabel(activities)} lose their category — the activities themselves stay.`
        : '';
  const draft =
    unsaved === 1 ? ' Your unsaved change goes with it.' : unsaved > 1 ? ` Your ${unsaved} unsaved changes go with it.` : '';
  return `“${title}” will be deleted.${consequence}${draft} This cannot be undone.`;
}

/** The delete refused by the server (`409 category_in_use`): not its activities — they only
 *  lose their category — but something else that still refers to it. */
export const CATEGORY_IN_USE_MESSAGE =
  'The category could not be deleted: something still refers to it. Reload and try again.';

/** The way back to the grid, in one phrase wherever the page offers it (the load states'
 *  `CatalogLoadError` words it the same). */
const BACK_LABEL = `Back to ${ACTIVITY_CATEGORY_RECORD.grid}`;

const BACK = (
  <Button component={RouterLink} to={ACTIVITY_CATEGORY_RECORD.gridHref} startIcon={<ArrowBackIcon />} size="small">
    {BACK_LABEL}
  </Button>
);

export function CategoryRecordPage() {
  const { id: idStr, tab } = useParams<{ id: string; tab?: string }>();
  const location = useLocation();
  const id = Number(idStr);
  if (!Number.isInteger(id) || id <= 0) {
    return (
      <RecordEmptyState icon={<SearchOffIcon />} title={`Activity category #${idStr ?? ''} not found`} action={BACK}>
        That is not an activity category id.
      </RecordEmptyState>
    );
  }
  const redirect = tab ? CATEGORY_SLUG_REDIRECTS[tab] : undefined;
  if (redirect) {
    return (
      <Navigate
        replace
        to={`/activity-categories/${id}${redirect === 'category' ? '' : `/${redirect}`}${location.search}`}
      />
    );
  }
  return <CategoryRecordLoader key={id} id={id} tabParam={tab} />;
}

/** The dock while the category loads, on the loaded dock's view-mode chrome: the "Changes"
 *  header over a hairline (its 30 px button room), then the note's lines. */
function ChangesDockSkeleton() {
  return (
    <Box
      data-changes-dock-skeleton
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', overflow: 'hidden' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 30, pl: 1.75, pr: 0.75, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" component="div">
          <Skeleton width={64} />
        </Typography>
      </Box>
      <Box sx={{ px: 1.75, py: 1.25 }}>
        <Typography variant="body2" component="div" sx={{ py: 1 }}>
          <Skeleton />
          <Skeleton />
          <Skeleton width="60%" />
        </Typography>
      </Box>
    </Box>
  );
}

/** The page's shape while the category loads: the header (icon tile, title, subtitle, the
 *  Actions and Edit buttons' places), the tabs, the card and the Activities panel — its rows
 *  for `activityCount` activities when the categories grid's cached row says how many — and,
 *  for an operator who will see Edit, from 1100 px of content, the Changes dock's column
 *  beside them; so the loaded page lands where the skeleton stood. History: one panel, no
 *  dock (it docks the ledger in edit mode only). */
function CategoryRecordLoading({
  edit,
  activityCount,
  history,
}: {
  edit: boolean;
  activityCount: number | null;
  history: boolean;
}) {
  const [ref, width] = useElementWidth<HTMLDivElement>();
  const docked = edit && !history && spineColumns(width).docked;
  return (
    <Box role="status" aria-label="Loading the category" data-category-loading>
      <CatalogHeaderSkeleton leading edit={edit} />
      <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 1 }} />
      {history ? (
        <Skeleton variant="rounded" height={320} data-tab-skeleton />
      ) : (
        <Box
          ref={ref}
          data-docked={docked || undefined}
          sx={{
            display: 'grid',
            gridTemplateColumns: docked ? `minmax(0, 1fr) ${LEDGER_WIDTH}px` : 'minmax(0, 1fr)',
            columnGap: `${LEDGER_GAP_PX}px`,
            alignItems: 'start',
          }}
        >
          <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
            <Card variant="outlined">
              <CardContent>
                <Skeleton variant="text" width={120} sx={{ mb: 1.5 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(232px, 1fr))', columnGap: 2 }}>
                  {[0, 1, 2].map((i) => (
                    <Box key={i}>
                      <Skeleton variant="text" width="45%" />
                      <Skeleton variant="rounded" height={40} />
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
            <ActivitiesInCategoryPanelSkeleton count={activityCount} />
          </Box>
          {docked && <ChangesDockSkeleton />}
        </Box>
      )}
    </Box>
  );
}

function CategoryRecordLoader({ id, tabParam }: { id: number; tabParam: string | undefined }) {
  const { hasPerm } = usePermissions();
  const qc = useQueryClient();
  const nav = useRecordNav<CategoryRecordTab>(`/activity-categories/${id}`, 'category');
  const detailQ = useActivityCategoryDetail(id, { retry: retryUnlessMissing });
  const detail = detailQ.data;
  if (!detail) {
    return detailQ.error ? (
      <CatalogLoadError kind={ACTIVITY_CATEGORY_RECORD} id={id} error={detailQ.error} onRetry={() => void detailQ.refetch()} />
    ) : (
      <CategoryRecordLoading
        edit={hasPerm(MANAGE_PERM)}
        activityCount={cachedActivityCount(qc, id)}
        // The History tab when the operator sees it (as the loaded page resolves the tab).
        history={tabParam === 'history' && hasPerm('console_audit_view')}
      />
    );
  }
  const tabs = categoryTabs(hasPerm('console_audit_view'));
  const activeTab: CategoryRecordTab = tabs.find((t) => t.id === tabParam)?.id ?? 'category';
  return (
    <CategoryRecordLoaded
      detail={detail}
      tabs={tabs}
      activeTab={activeTab}
      nav={nav}
      fetching={detailQ.isFetching}
      refreshError={detailQ.error}
      refetch={() => void detailQ.refetch()}
    />
  );
}

interface LoadedProps {
  detail: ActivityCategoryDetail;
  tabs: { id: CategoryRecordTab; label: string }[];
  activeTab: CategoryRecordTab;
  nav: RecordNav<CategoryRecordTab>;
  fetching: boolean;
  /** A refresh failed: the loaded copy stays under a notice. */
  refreshError: unknown;
  refetch: () => void;
}

interface PendingFocus {
  rec: RecordKey;
  path: string;
}

interface EditorProps {
  /** May edit now (the permission, the switch, an operator, the editor loaded). */
  canEdit: boolean;
  editing: boolean;
  /** The permission alone: Edit is offered at all. */
  mayManage: boolean;
  editingDisabled: boolean;
  /** With the permission and the switch on, why Edit is blocked (no operator, the editor
   *  failed to load). */
  editBlockedReason: string | null;
  editorError: unknown;
  retryEditor: () => void;
  pendingFocus: MutableRefObject<PendingFocus | null>;
}

/** The records core around the loaded category: the draft (edit_context's baselines, the
 *  detail's until they arrive) and the editing session. */
function CategoryRecordLoaded(props: LoadedProps) {
  const { detail, nav, activeTab } = props;
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const [params] = useSearchParams();
  const catalogQ = useActivityCategoryEditCatalog();
  const ctxQ = useActivityCategoryEditContext(id);
  const catalog = catalogQ.data ?? null;
  const ctx = ctxQ.data ?? null;
  const ready = catalog !== null && ctx !== null;
  // Only a failure that keeps the editor out: a failed refresh leaves the loaded data in use.
  const editorError = ready ? null : (catalogQ.error ?? ctxQ.error ?? null);

  // A ledger row for a field on History: back to the record tab, then focus it once its
  // FieldSlot has mounted.
  const pendingFocus = useRef<PendingFocus | null>(null);
  const tabRef = useRef(activeTab);
  tabRef.current = activeTab;
  const { goTab, setParam } = nav;
  const onFocusMissing = useCallback(
    (rec: RecordKey, path: string) => {
      if (tabRef.current === 'category') return;
      pendingFocus.current = { rec, path };
      goTab('category');
    },
    [goTab],
  );

  const detailBaselines = useMemo(() => categoryBaselines(detail), [detail]);
  const config = useCategoryEditorConfig({ detail, catalog, editContext: ctx, onFocusMissing });

  const mayManage = hasPerm(MANAGE_PERM) && (ctx ? ctx.permissions.console_activities_manage : true);
  const blocked = categoryEditBlockedReason({ catalog, editContext: ctx });
  const editingDisabled = blocked === CATALOG_EDITING_OFF;
  const editBlockedReason = !editingDisabled && blocked ? blocked : editorError ? CATALOG_EDITOR_LOAD_FAILED : null;
  const canEdit = mayManage && !editingDisabled && editBlockedReason === null;
  const editing = canEdit && ready && params.get('edit') === '1';
  const onExitEdit = useCallback(() => setParam('edit', null), [setParam]);

  const editor: EditorProps = {
    canEdit,
    editing,
    mayManage,
    editingDisabled,
    editBlockedReason,
    editorError,
    retryEditor: () => {
      void catalogQ.refetch();
      void ctxQ.refetch();
    },
    pendingFocus,
  };
  return (
    <DraftProvider
      kind="activity_category"
      recordId={id}
      baselines={ctx?.baselines ?? detailBaselines}
      baselineTs={ctx?.generated_ts ?? null}
    >
      <RecordEditorProvider mode={editing ? 'edit' : 'view'} config={config} onExitEdit={onExitEdit}>
        <CategoryRecordShell {...props} {...editor} />
      </RecordEditorProvider>
    </DraftProvider>
  );
}

/** The dock's title in view mode: what it holds once E is pressed. */
export const CHANGES_DOCK_TITLE = 'Changes';

/** The Changes dock in view mode — what it will hold, in the place and the type of the edit
 *  mode's empty ledger, so pressing E swaps the words and moves nothing: editing switched
 *  off, a permission the server refused (edit_context, after the dock stood), an editor that
 *  could not load, the changes a kept draft holds, or how to begin. */
export function ChangesAtRest({
  editingDisabled,
  refused = false,
  editBlockedReason,
  count,
}: {
  editingDisabled: boolean;
  /** The server says this operator may not manage the category. */
  refused?: boolean;
  editBlockedReason: string | null;
  count: number;
}) {
  let text: string;
  let note: string | null = null;
  if (refused) {
    text = `Needs the ${MANAGE_PERM} permission: nothing here can be changed.`;
  } else if (editingDisabled) {
    text = `${CATALOG_EDITING_OFF} on this environment: nothing here can be changed.`;
  } else if (editBlockedReason) {
    text = `${editBlockedReason}. Retry from the notice above the tabs.`;
  } else if (count > 0) {
    text = `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept — Continue editing to see, save or discard ${count === 1 ? 'it' : 'them'}.`;
  } else {
    text = 'No changes. Press E to edit the title, icon or access tags — every change lands here with what it triggers. Nothing is written until you save.';
    note = 'A category has no app preview: its title and icon show in the app’s activity browser.';
  }
  return (
    <Typography variant="body2" color="text.secondary" data-ledger-at-rest sx={{ py: 1 }}>
      {text}
      {note && (
        <Box component="span" sx={{ display: 'block', mt: 1, typography: 'caption' }}>
          {note}
        </Box>
      )}
    </Typography>
  );
}

/** Scroll an anchor into view and focus it (the card, the Activities panel). */
function reveal(anchorId: string) {
  const el = document.getElementById(anchorId);
  if (!el) return;
  el.scrollIntoView?.({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  el.focus({ preventScroll: true });
}

/** What the number keys reach on the record tab: `1` the card, `2` the Activities panel. */
const KEY_TARGETS = [categorySectionAnchorId('category'), CATEGORY_ACTIVITIES_ANCHOR_ID];

function CategoryRecordShell({
  detail,
  tabs,
  activeTab,
  nav,
  fetching,
  refreshError,
  refetch,
  canEdit,
  editing,
  mayManage,
  editingDisabled,
  editBlockedReason,
  editorError,
  retryEditor,
  pendingFocus,
}: LoadedProps & EditorProps) {
  const id = detail.id;
  const { hasPerm } = usePermissions();
  const canManage = hasPerm(MANAGE_PERM);
  const snackbar = useSnackbar();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { hrefFor, setParam } = nav;
  const onRecord = activeTab === 'category';
  const [contentRef, contentWidth] = useElementWidth<HTMLDivElement>();
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ---- the editor ------------------------------------------------------------------------
  const editor = useRecordEditor();
  const { restored, discardRestored, dispatch } = useDraft();
  const { count } = useDraftSummary();
  // Deleted: the draft went with the category, and the page is leaving.
  const [deleted, setDeleted] = useState(false);
  // Only leaving the category is guarded: its tabs share the draft.
  const { prompt } = useDraftUnsavedGuard(`/activity-categories/${id}`, { enabled: !deleted });
  const enterEdit = useCallback(() => setParam('edit', '1'), [setParam]);

  // A ledger row for a field on History lands here: focus it once the card renders.
  useEffect(() => {
    if (!onRecord || !pendingFocus.current) return undefined;
    // Taken when the frame runs: an update in between (a preview arriving) re-runs this
    // effect, whose cleanup cancels the frame — the pending focus must survive that.
    const raf = requestAnimationFrame(() => {
      const pending = pendingFocus.current;
      pendingFocus.current = null;
      if (pending) editor.focusField(pending.rec, pending.path);
    });
    return () => cancelAnimationFrame(raf);
  }, [onRecord, editor, pendingFocus]);

  // ---- Activities in this category: asked for once the panel has been on screen -------
  const [panelRef, seen] = useSeenOnce<HTMLDivElement>();
  const activitiesQ = useCategoryActivities(id, { enabled: seen, first: ACTIVITIES_FIRST });

  // `E` opens the editor; the number keys are the record tab's only (on History none is
  // registered). In edit mode the ledger binds ⌘S, ⌘Z / ⇧⌘Z and Esc.
  useRecordShortcuts(
    {
      ...(canEdit && !editing ? { onEdit: enterEdit } : {}),
      ...(onRecord
        ? {
            onStage: (i: number) => {
              const target = KEY_TARGETS[i];
              if (target) reveal(target);
            },
          }
        : {}),
    },
    { stageCount: onRecord ? KEY_TARGETS.length : 0 },
  );

  const deleteMutation = useMutation({
    mutationFn: () => categoriesApi.remove(id),
    onSuccess: () => {
      snackbar.show('Category deleted.', 'success');
      void qc.invalidateQueries({ queryKey: ['activity_categories'] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
      // The draft (and its stored copy) goes with the category; the page leaves once the
      // guard has let go of it (below).
      dispatch({ type: 'RESET' });
      setDeleted(true);
    },
    onError: (e) => {
      // 409 category_in_use → the page's words; otherwise the server's message.
      const code = e instanceof ConsoleApiError ? (e.body as { code?: string } | undefined)?.code : undefined;
      if (e instanceof ConsoleApiError && e.status === 409 && code === 'category_in_use') {
        snackbar.show(CATEGORY_IN_USE_MESSAGE, 'error');
        return;
      }
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Delete failed.', 'error');
    },
  });

  // After the guard's own effects (it is armed above): leaving is no longer blocked.
  const tenantId = detail.tenant.id;
  useEffect(() => {
    if (deleted) navigate(activityCategoriesHref({ tenant: tenantId }));
  }, [deleted, navigate, tenantId]);

  const actionGroups: RecordActionGroup[] = [
    {
      key: 'investigate',
      label: 'Investigate',
      items: [
        {
          key: 'activities',
          label: 'Open in Activities',
          hint: 'filtered grid',
          onSelect: () => navigate(categoryActivitiesHref(detail)),
        },
        // A real link: cmd-clickable, a new tab without the opener.
        ...(detail.dashboard_url
          ? [{ key: 'dashboard', label: 'Open in dashboard', hint: 'new tab', href: detail.dashboard_url }]
          : []),
      ],
    },
    ...(canManage
      ? [
          {
            key: 'danger',
            label: 'Danger',
            items: [{ key: 'delete', label: 'Delete…', danger: true, onSelect: () => setConfirmDelete(true) }],
          },
        ]
      : []),
  ];

  return (
    <Box data-record-page={id}>
      <CategoryRecordHeader
        detail={detail}
        section={SECTION_LABEL[activeTab]}
        canEdit={mayManage}
        editingDisabled={editingDisabled}
        editBlockedReason={editBlockedReason}
        editing={editing}
        onEdit={enterEdit}
        onClose={editor.requestClose}
        actionGroups={actionGroups}
      />

      {Boolean(refreshError) && (
        <CatalogRefreshError kind={ACTIVITY_CATEGORY_RECORD} error={refreshError} onRetry={refetch} />
      )}
      {Boolean(editorError) && mayManage && !editingDisabled && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          data-editor-error
          action={
            <Button color="inherit" size="small" onClick={retryEditor}>
              Retry
            </Button>
          }
        >
          {CATALOG_EDITOR_LOAD_FAILED}, so this category can’t be edited yet.
          {editorError instanceof Error && editorError.message ? ` ${editorError.message}` : ''}
        </Alert>
      )}
      {!editing && count > 0 && (
        <Alert
          severity="info"
          icon={<EditNoteOutlinedIcon />}
          sx={{ mb: 2 }}
          data-view-draft-banner
          action={
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {canEdit && (
                <Button color="inherit" size="small" onClick={enterEdit}>
                  Continue editing
                </Button>
              )}
              <Button color="inherit" size="small" onClick={restored ? discardRestored : () => dispatch({ type: 'DISCARD_ALL' })}>
                Discard
              </Button>
            </Box>
          }
        >
          {restored
            ? `Restored ${count} unsaved ${count === 1 ? 'change' : 'changes'} from ${formatZoned(restored.savedAt, CATEGORY_TIMEZONE, { weekday: false })} — not saved yet.`
            : `${count} unsaved ${count === 1 ? 'change is' : 'changes are'} kept in the editor — not saved yet.`}
        </Alert>
      )}

      <RecordPageTabs tabs={tabs} active={activeTab} hrefFor={hrefFor} fetching={fetching} {...TABS_LABELS} />

      <Box ref={contentRef}>
        <LedgerDock
          open={editing}
          // The Changes column stays in view mode, on every tab but History, for an operator
          // whose permissions (known at the first render, as the skeleton's) let them edit the
          // category: E changes what it says, never the layout. History docks it in edit mode
          // only.
          dockInView={activeTab !== 'history' && canManage}
          stickyTop={DOCK_GAP_PX - SCROLLER_PADDING_PX}
          viewportOffset={APPBAR_PX + SCROLLER_PADDING_PX}
          ledger={(variant) => (
            <Ledger
              variant={variant}
              mode={editing ? 'edit' : 'view'}
              viewTitle={CHANGES_DOCK_TITLE}
              {...(editing
                ? {}
                : {
                    body: (
                      <ChangesAtRest
                        editingDisabled={editingDisabled}
                        refused={canManage && !mayManage}
                        editBlockedReason={editBlockedReason}
                        count={count}
                      />
                    ),
                  })}
            />
          )}
        >
          {onRecord && (
            <Box sx={{ display: 'grid', gap: 2, minWidth: 0 }}>
              <CategoryCard detail={detail} width={contentWidth} />
              <Box ref={panelRef}>
                <ActivitiesInCategoryPanel
                  detail={detail}
                  activities={activitiesQ.data}
                  failed={Boolean(activitiesQ.error)}
                  onRetry={() => void activitiesQ.refetch()}
                />
              </Box>
            </Box>
          )}
          {activeTab === 'history' && (
            <HistoryPanel
              contentType="demo.activity_category"
              objectPk={String(id)}
              onRestored={() => {
                refetch();
                // A restore moves the stored values: the edit context's baselines follow.
                retryEditor();
              }}
            />
          )}
        </LedgerDock>
      </Box>

      <Box sx={{ mt: 4, color: 'text.secondary', fontSize: 13 }}>
        <RouterLink to={activityCategoriesHref({ tenant: detail.tenant.id })} style={{ color: '#0a1a3f' }}>
          ← {BACK_LABEL}
        </RouterLink>
      </Box>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this category?"
        message={categoryDeleteMessage(
          categoryTitle(detail),
          categoryActivityCount(activitiesQ.data, detail),
          count,
        )}
        confirmLabel="Delete category"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
      {prompt}
    </Box>
  );
}
